import { getLocalStorage } from '@/utils/localStorage';
import {
	ensureLeadJourneyId,
	getLeadJourneyCaseId,
	getLeadQualificationOutcome,
	getStoredUtmAttribution,
	isAttributionEnabledEnv,
} from './utmAttribution';

export type LeadJourneyEvent =
	| 'form_started'
	| 'form_submitted'
	| 'lead_qualified'
	| 'lead_did_not_qualify';
// | 'account_created';

const DEDUP_PREFIX = 'lead_journey_event_fired:';

declare global {
	interface Window {
		dataLayer?: Record<string, unknown>[];
	}
}

function hasTrackingConsent(): boolean {
	if (typeof window === 'undefined') return false;
	return getLocalStorage('cookie_consent') === true;
}

function initDataLayer(): void {
	if (typeof window === 'undefined') return;
	window.dataLayer = window.dataLayer || [];
}

function buildPayload(
	event: LeadJourneyEvent,
	extra?: Record<string, string | boolean | undefined>
): Record<string, unknown> {
	const attribution = getStoredUtmAttribution();
	const payload: Record<string, unknown> = {
		event,
		lead_journey_id: ensureLeadJourneyId(),
		...attribution,
	};

	const caseId = extra?.case_id ?? getLeadJourneyCaseId();
	if (caseId) {
		payload.case_id = caseId;
	}

	const qualificationOutcome =
		extra?.qualification_outcome ?? getLeadQualificationOutcome();
	if (qualificationOutcome) {
		payload.qualification_outcome = qualificationOutcome;
	}

	if (extra) {
		for (const [key, value] of Object.entries(extra)) {
			if (
				value !== undefined &&
				key !== 'case_id' &&
				key !== 'qualification_outcome'
			) {
				payload[key] = value;
			}
		}
	}

	return payload;
}

function wasEventFired(dedupKey: string): boolean {
	if (typeof window === 'undefined') return false;
	return sessionStorage.getItem(`${DEDUP_PREFIX}${dedupKey}`) === 'true';
}

function markEventFired(dedupKey: string): void {
	if (typeof window === 'undefined') return;
	sessionStorage.setItem(`${DEDUP_PREFIX}${dedupKey}`, 'true');
}

export function pushLeadJourneyEvent(
	event: LeadJourneyEvent,
	options?: {
		dedupKey?: string;
		extra?: Record<string, string | boolean | undefined>;
	}
): void {
	try {
		if (!isAttributionEnabledEnv()) return;
		if (!hasTrackingConsent()) return;

		const dedupKey = options?.dedupKey ?? event;
		if (wasEventFired(dedupKey)) return;

		initDataLayer();
		window.dataLayer?.push(buildPayload(event, options?.extra));
		markEventFired(dedupKey);
	} catch {
		// Tracking must never block user workflows.
	}
}
